import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { HookInventoryRow } from '@kirin/api-client/contracts/hooks';
import { queryKeys } from '@/hooks/api/queryKeys';
import { createHookColumns } from './columns';
import { fetchHooks } from './api';
import { HOOK_TABLE_CONFIG, HOOK_STALE_TIME_MS } from './constants';
import { HookDetailModal } from './HookDetailModal';
import { HookProjectsModal } from './HookProjectsModal';
import { HookUsersModal } from './HookUsersModal';
import { type HookTabScope, hookScopeKey } from './scope';

export function DataTableHooksContent({
  scope: scopeProp,
}: {
  scope: HookTabScope;
}): ReactElement {
  const [selectedDetailRow, setSelectedDetailRow] =
    useState<HookInventoryRow | null>(null);
  const [selectedProjectsKey, setSelectedProjectsKey] = useState<string | null>(
    null
  );
  const [selectedUsersKey, setSelectedUsersKey] = useState<string | null>(null);

  // Callers pass a fresh `scope` object literal each render. Rebuild a stable
  // one from its primitive parts so `columns` (and the DataTable `columns` prop)
  // keep a stable identity across parent re-renders — otherwise the table
  // re-mounts its rows on every render, detaching the Users/Projects count
  // buttons before a click can land (they read as "not clickable"). Keying on
  // the discriminant `scope.kind` (not a collapsed string) means an org whose id
  // were literally "account" is never misrouted to personal scope, and avoids a
  // stale closure over `scopeProp` without an eslint-disable. (KIR-3643)
  const scopeKind = scopeProp.kind;
  const scopeOrgId =
    scopeProp.kind === 'organization' ? scopeProp.organizationId : null;
  const scope = useMemo<HookTabScope>(
    () =>
      scopeKind === 'organization'
        ? { kind: 'organization', organizationId: scopeOrgId as string }
        : { kind: 'account' },
    [scopeKind, scopeOrgId]
  );
  const scopeKey = hookScopeKey(scope);

  const handleRowClick = useCallback((row: HookInventoryRow) => {
    setSelectedDetailRow(row);
  }, []);
  const handleProjectsClick = useCallback((groupKey: string) => {
    setSelectedProjectsKey(groupKey);
  }, []);
  const handleUsersClick = useCallback((groupKey: string) => {
    setSelectedUsersKey(groupKey);
  }, []);

  const columns = useMemo(
    () =>
      createHookColumns(scope, {
        onProjectsClick: handleProjectsClick,
        onUsersClick: handleUsersClick,
      }),
    [scope, handleProjectsClick, handleUsersClick]
  );

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) =>
      queryKeys.hooks.dataTable(scopeKey, {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      }),
    [scopeKey]
  );

  // Stable object identities for the DataTable props. Passing fresh object
  // literals here re-triggers the table's prop-keyed effects on every render,
  // which put the hooks table into a render loop that detached the row buttons
  // mid-click (Users/Projects counts read as "not clickable"). Mirrors the
  // Users tab, which memoizes these for the same reason. (KIR-3643)
  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchHooks(scope, params, signal),
    [scope]
  );
  const searchConfig = useMemo(
    () => ({ placeholder: 'Search hooks...', className: 'w-[280px]' }),
    []
  );
  const features = useMemo(() => ({ enableSorting: true }), []);
  const queryConfig = useMemo(
    () => ({ staleTime: HOOK_STALE_TIME_MS, refetchOnWindowFocus: false }),
    []
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Review discovered hooks. Includes coding-agent lifecycle hooks (Claude
          Code, Cursor, Copilot, and more), Git hooks from .git/hooks/, .husky/,
          and .pre-commit-config.yaml. Hooks with identical content but
          different events appear as separate rows.
        </AlertDescription>
      </Alert>
      <DataTable<HookInventoryRow>
        id={HOOK_TABLE_CONFIG.tableId}
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the hooks table"
        search={searchConfig}
        features={features}
        onRowClick={handleRowClick}
        queryFn={queryFn}
        getQueryKeysFromParams={getQueryKeysFromParams}
        noDataLabel="No hooks found."
        queryConfig={queryConfig}
      />
      <HookDetailModal
        scope={scope}
        hook={selectedDetailRow}
        onClose={() => setSelectedDetailRow(null)}
      />
      <HookUsersModal
        scope={scope}
        groupKey={selectedUsersKey}
        onClose={() => setSelectedUsersKey(null)}
      />
      <HookProjectsModal
        scope={scope}
        groupKey={selectedProjectsKey}
        onClose={() => setSelectedProjectsKey(null)}
      />
    </div>
  );
}

export default function DataTableHooksTab({
  scope,
}: {
  scope: HookTabScope;
}): ReactElement {
  return <DataTableHooksContent scope={scope} />;
}
